import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, ListItemSecondaryAction,
  ListItemIcon, Typography, IconButton, Button,
  CircularProgress, Chip, Box, Divider, Tooltip,
  InputAdornment, TextField, Collapse,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import * as Icons from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 3),
    borderBottom: '1px solid #e0e0e0',
  },
  titleText: {
    fontWeight: 700,
    color: '#222',
  },
  searchBox: {
    padding: theme.spacing(2, 3, 1),
  },
  content: {
    padding: 0,
    minHeight: 320,
    maxHeight: 480,
    overflowY: 'auto',
  },
  listItem: {
    padding: theme.spacing(1.5, 3),
    cursor: 'pointer',
    transition: 'background 0.15s',
    '&:hover': {
      backgroundColor: '#f0faf2',
    },
  },
  listItemSelected: {
    backgroundColor: '#e6f7ea',
    borderLeft: '4px solid #00b31f',
    '&:hover': {
      backgroundColor: '#d4f0da',
    },
  },
  // Fila en modo "confirmar borrado"
  listItemDeleting: {
    backgroundColor: '#fff3f3',
    borderLeft: '4px solid #f44336',
    cursor: 'default',
    '&:hover': {
      backgroundColor: '#fff3f3',
    },
  },
  homeIcon: {
    color: '#00b31f',
    minWidth: 36,
  },
  pageIcon: {
    color: '#9e9e9e',
    minWidth: 36,
  },
  primaryText: {
    fontWeight: 600,
    fontSize: '0.95rem',
  },
  slug: {
    fontSize: '0.78rem',
    color: '#888',
    marginTop: 2,
  },
  meta: {
    fontSize: '0.75rem',
    color: '#aaa',
    marginTop: 2,
  },
  chipPublished: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontWeight: 700,
    fontSize: '0.7rem',
    height: 22,
  },
  chipDraft: {
    backgroundColor: '#fff8e1',
    color: '#f57f17',
    fontWeight: 700,
    fontSize: '0.7rem',
    height: 22,
  },
  chipDefault: {
    backgroundColor: '#f5f5f5',
    color: '#757575',
    fontWeight: 700,
    fontSize: '0.7rem',
    height: 22,
  },
  actions: {
    padding: theme.spacing(1.5, 3),
    borderTop: '1px solid #e0e0e0',
    justifyContent: 'space-between',
  },
  emptyBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6, 2),
    gap: theme.spacing(1.5),
    color: '#aaa',
  },
  emptyIcon: {
    fontSize: '3rem',
    color: '#ccc',
  },
  loadingBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6, 2),
    gap: theme.spacing(2),
  },
  totalLabel: {
    fontSize: '0.8rem',
    color: '#888',
  },
  openButton: {
    backgroundColor: '#00b31f',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#009118',
    },
    '&:disabled': {
      backgroundColor: '#ccc',
      color: '#fff',
    },
  },
  deleteIcon: {
    color: '#f44336',
  },

  // ── Confirmación inline ───────────────────────────────────────────────────
  inlineConfirm: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 3, 1, 3),
    backgroundColor: '#fff3f3',
  },
  inlineConfirmText: {
    fontSize: '0.82rem',
    color: '#c62828',
    fontWeight: 500,
    flexGrow: 1,
  },
  confirmDeleteBtn: {
    backgroundColor: '#f44336',
    color: '#fff',
    fontSize: '0.75rem',
    padding: '3px 12px',
    minWidth: 'auto',
    '&:hover': {
      backgroundColor: '#c62828',
    },
    '&:disabled': {
      backgroundColor: '#ccc',
      color: '#fff',
    },
  },
  cancelDeleteBtn: {
    fontSize: '0.75rem',
    padding: '3px 10px',
    minWidth: 'auto',
    color: '#555',
  },
}));

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function statusChipProps(status, classes) {
  switch ((status || '').toLowerCase()) {
    case 'published': return { label: 'Publicado', className: classes.chipPublished };
    case 'draft':     return { label: 'Borrador',  className: classes.chipDraft };
    default:          return { label: status || 'Sin estado', className: classes.chipDefault };
  }
}

// ---------------------------------------------------------------------------
// PageListItem — fila individual con confirmación inline
// ---------------------------------------------------------------------------
function PageListItem({ page, isSelected, onSelect, onDelete, isDeleting, classes }) {
  const [pendingDelete, setPendingDelete] = useState(false);

  const handleAskDelete = (e) => {
    e.stopPropagation();
    setPendingDelete(true);
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    setPendingDelete(false);
  };

  const handleConfirmDelete = async (e) => {
    e.stopPropagation();
    await onDelete(page);
    setPendingDelete(false);
  };

  const chip = statusChipProps(page.status, classes);
  const rowClass = pendingDelete
    ? classes.listItemDeleting
    : isSelected
      ? `${classes.listItem} ${classes.listItemSelected}`
      : classes.listItem;

  return (
    <>
      <ListItem
        className={rowClass}
        onClick={pendingDelete ? undefined : () => onSelect(page)}
        disableRipple={pendingDelete}
      >
        <ListItemIcon className={page.isHomePage ? classes.homeIcon : classes.pageIcon}>
          {page.isHomePage ? <Icons.Home /> : <Icons.InsertDriveFile />}
        </ListItemIcon>

        <ListItemText
          primary={
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Typography
                className={classes.primaryText}
                noWrap
                style={pendingDelete ? { color: '#c62828', textDecoration: 'line-through' } : {}}
              >
                {page.title || '(Sin título)'}
              </Typography>
              {!pendingDelete && <Chip size="small" {...chip} />}
              {!pendingDelete && page.isHomePage && (
                <Chip
                  size="small"
                  label="Inicio"
                  style={{ backgroundColor: '#e3f2fd', color: '#1565c0', fontSize: '0.7rem', height: 22, fontWeight: 700 }}
                />
              )}
              {!pendingDelete && page.pageType === 'dynamic_list' && (
                <Chip
                  size="small"
                  label="Listado"
                  style={{ backgroundColor: '#fff3e0', color: '#e65100', fontSize: '0.7rem', height: 22, fontWeight: 700 }}
                />
              )}
              {!pendingDelete && page.pageType === 'dynamic_detail' && (
                <Chip
                  size="small"
                  label="Detalle"
                  style={{ backgroundColor: '#f3e5f5', color: '#6a1b9a', fontSize: '0.7rem', height: 22, fontWeight: 700 }}
                />
              )}
            </Box>
          }
          secondary={
            !pendingDelete && (
              <>
                <Typography component="span" display="block" className={classes.slug}>
                  /{page.slug || ''}
                </Typography>
                <Typography component="span" display="block" className={classes.meta}>
                  Actualizado {page.updatedAt ? moment(page.updatedAt).fromNow() : '—'}
                  {page.createdAt && ` · Creado ${moment(page.createdAt).format('DD/MM/YYYY')}`}
                </Typography>
              </>
            )
          }
        />

        <ListItemSecondaryAction>
          {pendingDelete ? (
            // En modo confirmación: botones Cancelar / Confirmar
            <Box display="flex" alignItems="center" style={{ gap: 4 }}>
              <Button
                size="small"
                className={classes.cancelDeleteBtn}
                onClick={handleCancelDelete}
                disabled={isDeleting}
              >
                No, cancelar
              </Button>
              <Button
                size="small"
                variant="contained"
                className={classes.confirmDeleteBtn}
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                startIcon={
                  isDeleting
                    ? <CircularProgress size={12} style={{ color: '#fff' }} />
                    : <Icons.Delete style={{ fontSize: 14 }} />
                }
              >
                {isDeleting ? 'Eliminando…' : 'Sí, eliminar'}
              </Button>
            </Box>
          ) : (
            // Estado normal: seleccionar + borrar
            <Box display="flex" alignItems="center">
              <Tooltip title={isSelected ? 'Seleccionado' : 'Seleccionar'}>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onSelect(page); }}>
                  {isSelected
                    ? <Icons.CheckCircle style={{ color: '#00b31f' }} />
                    : <Icons.RadioButtonUnchecked style={{ color: '#ccc' }} />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Eliminar página">
                <IconButton
                  size="small"
                  className={classes.deleteIcon}
                  onClick={handleAskDelete}
                >
                  <Icons.DeleteOutline />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </ListItemSecondaryAction>
      </ListItem>
    </>
  );
}

// ---------------------------------------------------------------------------
// DesignListDialog
// ---------------------------------------------------------------------------
export default function DesignListDialog({ open, onClose, onSelect, listDesigns, isListing, deleteDesign, isDeleting }) {
  const classes = useStyles();

  const [pages,      setPages]      = useState([]);
  const [filtered,   setFiltered]   = useState([]);
  const [search,     setSearch]     = useState('');
  const [selected,   setSelected]   = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [deleteErrorMsg, setDeleteErrorMsg] = useState('');

  // ── Fetch al abrir ────────────────────────────────────────────────────────
  const fetchPages = useCallback(async () => {
    setFetchError(null);
    setSelected(null);
    setSearch('');
    setDeleteErrorMsg('');

    const result = await listDesigns();
    if (result.success) {
      setPages(result.pages);
      setFiltered(result.pages);
    } else {
      setFetchError(result.error || 'No se pudieron cargar los diseños.');
    }
    setHasFetched(true);
  }, [listDesigns]);

  useEffect(() => {
    if (open) {
      setHasFetched(false);
      fetchPages();
    }
  }, [open]); // eslint-disable-line

  // ── Filtro de búsqueda ────────────────────────────────────────────────────
  useEffect(() => {
    if (!search.trim()) {
      setFiltered(pages);
      return;
    }
    const term = search.toLowerCase();
    setFiltered(
      pages.filter(
        (p) =>
          (p.title  || '').toLowerCase().includes(term) ||
          (p.slug   || '').toLowerCase().includes(term) ||
          (p.status || '').toLowerCase().includes(term)
      )
    );
  }, [search, pages]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSelect = (page) => {
    setSelected((prev) => (prev?.pageId === page.pageId ? null : page));
  };

  const handleOpen = () => {
    if (selected) onSelect(selected);
  };

  const handleClose = () => {
    setSelected(null);
    setSearch('');
    setDeleteErrorMsg('');
    onClose();
  };

  // Borrado: llamado desde PageListItem, sin abrir ningún dialog adicional
  const handleDelete = async (page) => {
    setDeleteErrorMsg('');
    const result = await deleteDesign(page.pageId);
    if (result.success) {
      // Si la página borrada era la seleccionada, limpiar selección
      if (selected?.pageId === page.pageId) setSelected(null);
      fetchPages();
    } else {
      setDeleteErrorMsg(result.error || 'No se pudo eliminar la página.');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const renderContent = () => {
    if (isListing && !hasFetched) {
      return (
        <Box className={classes.loadingBox}>
          <CircularProgress style={{ color: '#00b31f' }} />
          <Typography variant="body2" color="textSecondary">
            Cargando páginas…
          </Typography>
        </Box>
      );
    }

    if (fetchError) {
      return (
        <Box p={3}>
          <Alert
            severity="error"
            action={
              <Button size="small" onClick={fetchPages} disabled={isListing}>
                Reintentar
              </Button>
            }
          >
            {fetchError}
          </Alert>
        </Box>
      );
    }

    if (filtered.length === 0) {
      return (
        <Box className={classes.emptyBox}>
          <Icons.FolderOpen className={classes.emptyIcon} />
          <Typography variant="body1" style={{ fontWeight: 600, color: '#555' }}>
            {search ? 'Sin resultados para tu búsqueda' : 'Aún no tienes páginas creadas'}
          </Typography>
          <Typography variant="body2">
            {search
              ? 'Intenta con otro término de búsqueda.'
              : 'Crea una nueva página desde el editor para comenzar.'}
          </Typography>
        </Box>
      );
    }

    return (
      <List disablePadding>
        {filtered.map((page, idx) => (
          <React.Fragment key={page.pageId}>
            {idx > 0 && <Divider component="li" />}
            <PageListItem
              page={page}
              isSelected={selected?.pageId === page.pageId}
              onSelect={handleSelect}
              onDelete={handleDelete}
              isDeleting={isDeleting}
              classes={classes}
            />
          </React.Fragment>
        ))}
      </List>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>

      {/* ── Título ── */}
      <Box className={classes.dialogTitle}>
        <Typography variant="h6" className={classes.titleText}>
          <Icons.Collections style={{ verticalAlign: 'middle', marginRight: 8, color: '#00b31f' }} />
          Mis páginas
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          {hasFetched && (
            <Typography className={classes.totalLabel}>
              {pages.length} {pages.length === 1 ? 'página' : 'páginas'}
            </Typography>
          )}
          <Tooltip title="Actualizar lista">
            <span>
              <IconButton size="small" onClick={fetchPages} disabled={isListing}>
                {isListing
                  ? <CircularProgress size={18} style={{ color: '#00b31f' }} />
                  : <Icons.Refresh />}
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={handleClose}>
            <Icons.Close />
          </IconButton>
        </Box>
      </Box>

      {/* ── Búsqueda ── */}
      {hasFetched && pages.length > 0 && (
        <Box className={classes.searchBox}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Buscar por título, slug o estado…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icons.Search style={{ color: '#aaa', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}>
                    <Icons.Clear style={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {/* ── Error de borrado (inline, sin modal) ── */}
      {deleteErrorMsg && (
        <Box px={3} pt={1}>
          <Alert severity="error" onClose={() => setDeleteErrorMsg('')}>
            {deleteErrorMsg}
          </Alert>
        </Box>
      )}

      {/* ── Lista ── */}
      <DialogContent className={classes.content} dividers={false}>
        {renderContent()}
      </DialogContent>

      {/* ── Acciones ── */}
      <DialogActions className={classes.actions}>
        <Button onClick={handleClose} variant="outlined">
          Cancelar
        </Button>
        <Button
          variant="contained"
          className={classes.openButton}
          onClick={handleOpen}
          disabled={!selected}
          startIcon={<Icons.Edit />}
        >
          Abrir en el editor
        </Button>
      </DialogActions>
    </Dialog>
  );
}